import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowUp, Check, Plus, Search, X } from "lucide-react";
import { T } from "../lib/theme";

/* ---------------------------------------------------------------- */
/* Chrome                                                           */
/* ---------------------------------------------------------------- */

export function Header({
  title,
  sub,
  right,
  subDim,
}: {
  title: string;
  sub?: ReactNode;
  right?: ReactNode;
  /** Dims the subtitle. Up Next uses it for the row count, which was
   *  deliberately de-emphasised rather than removed — it still confirms a
   *  filter did something. */
  subDim?: boolean;
}) {
  return (
    <div style={{ background: T.navy, color: "#fff", padding: "16px 18px 18px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow" style={{ color: "rgba(255,255,255,.5)" }}>
            Sacrament meeting
          </div>
          <h1
            className="serif"
            style={{ margin: "3px 0 0", fontSize: 25, fontWeight: 500, letterSpacing: "-.01em" }}
          >
            {title}
          </h1>
          {sub && (
            <div
              style={{
                fontSize: subDim ? 11.5 : 12.5,
                color: subDim ? "rgba(255,255,255,.42)" : "rgba(255,255,255,.55)",
                marginTop: 3,
              }}
            >
              {sub}
            </div>
          )}
        </div>
        {right}
      </div>
    </div>
  );
}

/** The part of the page actually visible — shrinks when the on-screen keyboard
 *  opens. null where the API is missing, and callers fall back to full height. */
function useVisualViewport() {
  const [box, setBox] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    const v = window.visualViewport;
    if (!v) return;
    const update = () => setBox({ top: v.offsetTop, height: v.height });
    update();
    v.addEventListener("resize", update);
    v.addEventListener("scroll", update);
    return () => {
      v.removeEventListener("resize", update);
      v.removeEventListener("scroll", update);
    };
  }, []);
  return box;
}

export function Sheet({
  children,
  onClose,
  z = 70,
  tall = false,
}: {
  children: ReactNode;
  onClose: () => void;
  /** Raise above another open sheet — see ConfirmSheet. */
  z?: number;
  /** Fixed height instead of shrinking to fit. For a searchable list, so the
   *  search box doesn't slide down under your thumb as results filter away. */
  tall?: boolean;
}) {
  const vv = useVisualViewport();

  // Escape closes, and body scroll is locked so the page behind doesn't move
  // under the sheet on iOS.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="fade-in"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        // Track the visible area rather than inset: 0. Mobile browsers open the
        // keyboard by shrinking the visual viewport only, so a full-height
        // bottom-anchored sheet ends up behind the keyboard.
        top: vv?.top ?? 0,
        height: vv?.height ?? "100%",
        background: "rgba(11,37,69,.42)",
        zIndex: z,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="sheet-in"
        style={{
          background: "#fff",
          width: "100%",
          maxWidth: 520,
          borderRadius: "18px 18px 0 0",
          // % of the backdrop, not vh: vh ignores the keyboard, so 86vh can be
          // taller than the space left above it and push the top off-screen.
          maxHeight: "86%",
          height: tall ? "86%" : undefined,
          overflowY: "auto",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function SheetHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <h2 className="serif" style={{ margin: 0, fontSize: 21, fontWeight: 500 }}>
        {title}
      </h2>
      <button onClick={onClose} aria-label="Close" style={{ color: T.muted, display: "flex", padding: 2 }}>
        <X size={20} />
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Controls                                                         */
/* ---------------------------------------------------------------- */

export function Chip({
  on,
  onClick,
  children,
  tone,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  tone?: "brass";
}) {
  const activeBg = tone === "brass" ? T.brass : "#fff";
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      style={{
        padding: "7px 13px",
        borderRadius: 8,
        fontSize: 12.5,
        fontWeight: 600,
        background: on ? activeBg : "rgba(255,255,255,.10)",
        color: on ? (tone === "brass" ? "#fff" : T.navy) : "rgba(255,255,255,.6)",
      }}
    >
      {children}
    </button>
  );
}

/** Search input styled for the navy header. Always has a clear button. */
export function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ position: "relative" }}>
      <Search size={16} style={{ position: "absolute", left: 12, top: 12, color: T.faint }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder ?? "Search"}
        style={{
          width: "100%",
          padding: "10px 40px 10px 34px",
          borderRadius: 10,
          border: "none",
          background: "rgba(255,255,255,.12)",
          color: "#fff",
          fontSize: 16,
        }}
      />
      {value && (
        <button
          onClick={() => onChange("")}
          aria-label="Clear search"
          style={{ position: "absolute", right: 4, top: 3, padding: 8, color: "rgba(255,255,255,.6)", display: "flex" }}
        >
          <X size={17} />
        </button>
      )}
    </div>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        border: `1px solid ${T.line}`,
      }}
    >
      {children}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="eyebrow" style={{ marginBottom: 7 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

export function Note({ children, tone }: { children: ReactNode; tone?: "brass" }) {
  return (
    <div
      style={{
        background: tone === "brass" ? T.brassSoft : T.accentSoft,
        color: tone === "brass" ? T.brass : T.accent,
        borderRadius: 8,
        padding: "9px 11px",
        fontSize: 12.5,
        marginBottom: 14,
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

export function YouthTag() {
  return (
    <span
      style={{
        fontFamily: "Inter",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: ".08em",
        color: T.accent,
        background: T.accentSoft,
        padding: "2px 5px",
        borderRadius: 4,
        marginLeft: 7,
        verticalAlign: "middle",
      }}
    >
      YOUTH
    </span>
  );
}

/* ---------------------------------------------------------------- */
/* Floating buttons                                                 */
/* ---------------------------------------------------------------- */

/** `raised` stacks it above the add button on the Members screen, where the two
 *  would otherwise collide. */
export function ScrollTopButton({ raised }: { raised?: boolean }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // ~5 rows in. 700px meant scrolling past a dozen names before the button
    // turned up, by which point you've already started flicking back by hand.
    const onScroll = () => setShow(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      className="fade-in"
      style={{
        position: "fixed",
        bottom: raised ? 148 : 92,
        right: 16,
        zIndex: 55,
        width: 44,
        height: 44,
        borderRadius: 22,
        background: "#fff",
        border: `1px solid ${T.line}`,
        color: T.navy,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 16px rgba(11,37,69,.16)",
      }}
    >
      <ArrowUp size={20} strokeWidth={2.2} />
    </button>
  );
}

/** Floating add button. It matters more than it looks: after scrolling 200 names
 *  looking for someone who turns out not to be listed, the header button is long
 *  gone — and that is exactly when you want to add them. */
export function FabAdd({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        position: "fixed",
        bottom: 92,
        right: 16,
        zIndex: 55,
        width: 52,
        height: 52,
        borderRadius: 26,
        background: T.navy,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 6px 20px rgba(11,37,69,.32)",
      }}
    >
      <Plus size={24} strokeWidth={2.4} />
    </button>
  );
}

/* ---------------------------------------------------------------- */
/* States                                                           */
/* ---------------------------------------------------------------- */

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: "46px 24px", textAlign: "center", color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div
      style={{
        padding: "70px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div className="spinner" aria-label={label ?? "Loading"} />
      {label && <div style={{ fontSize: 13.5, color: T.muted }}>{label}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const msg = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div style={{ padding: "40px 24px", textAlign: "center" }}>
      <div className="serif" style={{ fontSize: 19, color: T.ink, marginBottom: 8 }}>
        Couldn't load
      </div>
      <div style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.55, marginBottom: 20 }}>{msg}</div>
      <button
        onClick={onRetry}
        style={{
          padding: "11px 22px",
          borderRadius: 10,
          background: T.navy,
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        Try again
      </button>
    </div>
  );
}

export function Toast({ message, tone }: { message: string; tone?: "error" }) {
  return (
    <div
      className="fade-in"
      role="status"
      style={{
        position: "fixed",
        bottom: 92,
        left: "50%",
        transform: "translateX(-50%)",
        background: tone === "error" ? "#8C2F26" : T.navy,
        color: "#fff",
        padding: "11px 18px",
        borderRadius: 999,
        fontSize: 13.5,
        fontWeight: 500,
        zIndex: 90,
        display: "flex",
        gap: 8,
        alignItems: "center",
        maxWidth: "calc(100vw - 32px)",
        boxShadow: "0 8px 28px rgba(11,37,69,.3)",
      }}
    >
      {tone === "error" ? <X size={15} /> : <Check size={15} />}
      {message}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Virtualised list                                                 */
/* ---------------------------------------------------------------- */

/** Renders every row's worth of height but mounts only what's on screen.
 *
 *  This measures against the *window*, not an inner scrolling element, which is
 *  what lets the page scroll naturally on mobile instead of trapping scroll in a
 *  div. The consequence: the container must stay in normal document flow — don't
 *  wrap it in anything with its own overflow.
 *
 *  All ~350 rows are addressable on purpose. The owner explicitly wanted the
 *  full list available, especially for alphabetical browsing; an earlier
 *  "show 60 more" pagination was removed at their request.
 */
export function VirtualList<Item>({
  items,
  rowHeight,
  renderRow,
  overscan = 8,
}: {
  items: Item[];
  rowHeight: number;
  renderRow: (item: Item, index: number) => ReactNode;
  overscan?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: 30 });

  useEffect(() => {
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const first = Math.floor((window.scrollY - top) / rowHeight) - overscan;
      const count = Math.ceil(window.innerHeight / rowHeight) + overscan * 2;
      const start = Math.max(0, first);
      setRange({ start, end: Math.min(items.length, start + count) });
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [items.length, rowHeight, overscan]);

  const slice = items.slice(range.start, range.end);

  return (
    <div ref={ref} style={{ height: items.length * rowHeight, position: "relative" }}>
      <div style={{ transform: `translateY(${range.start * rowHeight}px)` }}>
        {slice.map((item, i) => renderRow(item, range.start + i))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Confirmation                                                     */
/* ---------------------------------------------------------------- */

/** For the few actions that can't be undone by repeating them.
 *
 *  Used sparingly on purpose: confirming routine actions is how people learn to
 *  tap through dialogs without reading, which is precisely when the one that
 *  matters stops working.
 */
export function ConfirmSheet({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Sheet onClose={onCancel} z={80}>
      <div style={{ padding: 20 }}>
        <h2 className="serif" style={{ margin: "0 0 8px", fontSize: 21, fontWeight: 500 }}>
          {title}
        </h2>
        <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.55, marginBottom: 20 }}>{body}</div>

        <button
          onClick={onConfirm}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 11,
            background: "#8C2F26",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          {confirmLabel}
        </button>
        <button
          onClick={onCancel}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 11,
            border: `1px solid ${T.line}`,
            color: T.ink,
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
